import { memo, useContext, useMemo } from 'react';

import snakeCase from 'lodash.snakecase';
import styled, { type CSSObject } from '@emotion/styled';

import type { ThemeProps } from '@emotion/react';

import { cleanUserInput } from '../../lib/cleanUserInput';
import dateTimeFormatted from '../../lib/dateFormatter/dateTimeFormatted';
import { themeFont } from '../../lib/themeFont';
import { ActionButtonLink } from '../ActionButton';
import { P } from './ParagraphItem';

import {
  isAccommodationItem,
  isWwsWeddingWebsiteV2,
  type Wws_BusinessItem,
} from '../../types/graphql';
import { WWContext, WWContextValue } from '../WWContext';
import { useCurrentRoute } from '../../lib/useCurrentRoute';
import useTracking from '../../lib/useTracking';
import FixedNextImage from '../NextImage/FixedNextImage';

export const Item = styled('div')`
  padding: 1px 10px;
  max-width: 550px;
  margin: 0 auto 12px;
  text-align: center;
  @media (min-width: 768px) {
    .verticalLayout.homePage & {
      padding: 1px 0;
      margin: 0 auto 12px 0;
    }
  }
  ${(props: ThemeProps): CSSObject | undefined =>
    props.theme.components.ContentItem}
`;

export const Name = styled('h4')`
  color: rgb(31, 31, 31);
  line-height: 1;
  margin: 28px 0;

  ${themeFont('s3')}

  @media (min-width: 768px) {
    text-align: center;

    .verticalLayout.homePage & {
      text-align: left;
    }
  }
`;

export const ItemDescription = styled(P)`
  line-height: 1.75;
  margin: 32px 0;

  ${themeFont('s6')}

  a {
    text-decoration: underline;
    ${(props: ThemeProps): CSSObject | undefined =>
      props.theme.components.Link};
  }
`;

const Info = styled('div')`
  margin: 16px 0;
`;

const Address = styled('div')`
  font-style: normal;
  line-height: 1;

  ${themeFont('s5')}
`;

const PhoneNumber = styled('div')`
  font-style: normal;
  line-height: 1;
  margin-top: 8px;

  ${themeFont('s5')}
`;

const CheckInOut = styled('span')`
  font-style: normal;
  line-height: 1;

  ${themeFont('s6')}
`;

const AccomTitle = styled('span')`
  font-weight: bold;

  ${themeFont('s6')}

  word-break: break-word;
`;

const AccomInfo = styled('div')`
  margin: 16px 0;

  ${themeFont('s6')}
`;

const Actions = styled('div')`
  display: flex;
  justify-content: center;
  margin: 20px 0;
  flex-wrap: wrap;
  row-gap: 10px;
`;

interface BusinessItemProps {
  item: Wws_BusinessItem;
  className?: string | undefined;
}

export const buildAbsoluteWebsiteUrl = (
  websiteUrl?: string | null,
  slug?: string
) => {
  if (!websiteUrl) {
    return;
  }

  let absoluteWebsiteUrl = websiteUrl;
  if (!websiteUrl.match(/\/\//)) {
    absoluteWebsiteUrl = `//${websiteUrl}`;
  }

  if (slug) {
    const hostURL = process.env['WWS_HOST']?.replace('https://', '');
    const concatValue = absoluteWebsiteUrl.includes('?') ? '&' : '?';

    absoluteWebsiteUrl = `${absoluteWebsiteUrl}${concatValue}wwpath=${encodeURIComponent(
      `${hostURL}/${slug}`
    )}`;
  }

  return absoluteWebsiteUrl;
};

export const buildRelString = (item: Wws_BusinessItem) => {
  const defaultRel = 'nofollow ugc noopener noreferrer';

  if (
    isAccommodationItem(item) &&
    isUrlSponsored(item.website, item.provider)
  ) {
    return `${defaultRel} sponsored`;
  }
  return defaultRel;
};

export const isUrlSponsored = (
  url?: string | null,
  provider?: string | null
) => {
  if (!url) {
    return false;
  }

  return (
    !!provider ||
    url?.includes('engine.com') ||
    url?.includes('hotelblocks.theknot.com')
  );
};

export const handleClickingWebsiteLink = (
  item: Wws_BusinessItem,
  trackClickingWebsiteLink: (
    selection: string,
    trackArea?: string | undefined
  ) => void
): void => {
  if (!isAccommodationItem(item)) {
    return;
  }

  const provider = item.provider || 'hotel';
  trackClickingWebsiteLink(`${provider} link clicked`);
};

const buildBaseMediaUrl = (mediaUrl: string): string | undefined => {
  if (!mediaUrl || mediaUrl.includes('http')) {
    return mediaUrl;
  }

  return `https:${mediaUrl}`;
};

// TODO: make 3 separate components for each Item type
function BusinessItem({ item, className }: BusinessItemProps) {
  const { id, type, description, name, address, phone, email, website, photo } =
    item;

  const mediaUrl = photo?.mediaUrl || '';

  const isAccommodationItemObj = isAccommodationItem(item);

  let checkInDate;
  let checkOutDate;
  let guestCode;
  let rate;
  let slug;

  const { data } = useContext<WWContextValue>(WWContext);
  const currentRoute = useCurrentRoute();
  const userDecisionArea = isAccommodationItemObj
    ? 'hotel website link'
    : 'business website link';
  const trackClickingWebsiteLink = useTracking(
    'guest',
    currentRoute,
    userDecisionArea
  );

  if (isAccommodationItemObj) {
    checkInDate = dateTimeFormatted(item.checkInDate, 'weekday');

    checkOutDate = dateTimeFormatted(item.checkOutDate, 'weekday');

    guestCode = item.guestCode;

    rate = item.rate;

    if (website) {
      if (isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
        slug = data?.weddingWebsiteResult?.slug;
      }
    }
  }

  const baseMediaUrl = buildBaseMediaUrl(mediaUrl);

  const absoluteWebsite = buildAbsoluteWebsiteUrl(website, slug);

  const dangerouslySetInnerHTML = useMemo<{
    __html: string;
  }>(
    () => ({
      __html: cleanUserInput(description),
    }),
    [description]
  );

  return (
    <Item
      className={className}
      data-testid={`${snakeCase(type).replace(/_/g, '-')}-${id}`}
    >
      {name ? <Name>{name}</Name> : null}

      {!!baseMediaUrl && (
        <FixedNextImage
          src={baseMediaUrl}
          width={item.photo?.width ?? 300}
          height={item.photo?.height ?? 300}
          alt={`Picture of ${name}`}
        />
      )}

      {address || phone ? (
        <Info>
          {address ? <Address>{address}</Address> : null}

          {phone ? <PhoneNumber>{phone}</PhoneNumber> : null}
        </Info>
      ) : null}

      {checkInDate || checkOutDate ? (
        <AccomInfo>
          {checkInDate ? (
            <CheckInOut>
              <AccomTitle>Check-In: </AccomTitle>
              {checkInDate}
            </CheckInOut>
          ) : null}

          {checkInDate && checkOutDate ? ' | ' : null}

          {checkOutDate ? (
            <CheckInOut>
              <AccomTitle>Check-Out: </AccomTitle>
              {checkOutDate}
            </CheckInOut>
          ) : null}
        </AccomInfo>
      ) : null}

      {rate ? (
        <Info>
          <AccomTitle>Rate: {rate}</AccomTitle>
        </Info>
      ) : null}

      {guestCode ? (
        <Info>
          <AccomTitle>Use Code: {guestCode}</AccomTitle>
        </Info>
      ) : null}

      {description ? (
        <ItemDescription dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : null}

      {(absoluteWebsite || email) && (
        <Actions>
          {absoluteWebsite && (
            <ActionButtonLink
              className={
                item.website?.includes('/reservation/') ? 'noskim' : undefined
              }
              target="_blank"
              href={absoluteWebsite}
              rel={buildRelString(item)}
              data-testid={`business-website-${id}`}
              onClick={() =>
                handleClickingWebsiteLink(item, trackClickingWebsiteLink)
              }
            >
              Website
            </ActionButtonLink>
          )}

          {email && (
            <ActionButtonLink
              rel="nofollow ugc noopener noreferrer"
              href={`mailto:${email}`}
            >
              Email
            </ActionButtonLink>
          )}
        </Actions>
      )}
    </Item>
  );
}

export default memo(BusinessItem);
